import React from "react";
import { useGamesListReturnAnchor } from "@/v2-components/pages/games/use-games-list-return-anchor";
import { GetServerSidePropsContext } from "next";
import GamesPageTemplate from "@/components/templates/GamesPageTemplate";
import GamesSubNav from "@/v2-components/pages/games/GamesSubNav";
import VrGamesFilterRail from "@/v2-components/pages/games/vr-games/vr-games-filter-rail";
import VrGamesFilterSheet from "@/v2-components/pages/games/vr-games/vr-games-filter-sheet";
import VrGamesSearch from "@/v2-components/pages/games/vr-games/vr-games-search";
import VrGamesList from "@/v2-components/pages/games/vr-games/vr-games-list";
import { useVrGamesFilters } from "@/v2-components/pages/games/vr-games/use-vr-games-filters";
import { getGamesServerSideProps } from "@/libs/getGamesServerSideProps";
import { useTrans } from "@/utils/useTrans";
import PcSearchDropdown from "@/v2-components/pages/games/pc-games/pc-search-dropdown";
import PcGamesEmpty from "@/v2-components/pages/games/pc-games/pc-games-empty";
import { usePcSearchBox } from "@/v2-components/pages/games/pc-games/use-pc-search-box";
import { useVrSearchStates } from "@/v2-components/pages/games/pc-games/use-pc-search-states";
import { useGameRequestVote } from "@/v2-components/pages/games/pc-games/use-game-request-vote";
import { useStickyBandBottom } from "@/v2-components/pages/games/use-sticky-band-bottom";
import { MOBILE_FILTER_ROW_SELECTOR } from "@/v2-components/pages/games/sticky-band";
import PcUpcomingAndNew from "@/v2-components/pages/games/pc-games/PcUpcomingAndNew";
import { VR_PLATFORM_VALUES } from "@/constants/game-filter-options";

/**
 * 폭 규약 — `justify-center` + `mx-4 flex-1 min-w-0 max-w-screen-max`.
 *
 * ★상수로 뺀 이유: 「Upcoming & New」 섹션이 **자기 컨테이너를 갖고** 들어오면서 이
 * 컨테이너가 그 앞뒤로 **두 번** 열리게 됐다. 문자열을 두 곳에 적으면 한쪽만 고쳤을 때
 * 헤더와 본문의 좌우가 조용히 어긋난다.
 *
 * 종전엔 `mx-auto ... px-4 laptop:px-8` 이라 **`max-w` 안쪽이 깎였다** — 1920 에서
 * 컨테이너는 1440 인데 내부 패딩 64 때문에 콘텐츠가 **1376** 이 되고, 레일 342 + 24 +
 * 목록 1010 으로 시안(360 + 24 + 1056 = 1440)과 어긋났다.
 * 사유 원문은 `PcOverviewHero.tsx`, 같은 규약을 VR Overview 전 섹션이 쓴다.
 */
const VR_GAMES_CONTAINER = "mx-4 flex min-w-0 max-w-screen-max flex-1 flex-col";

/**
 * VR Games 탭의 「지원 게임」 화면 (#5416 · 기획 #5363 · 시안 `29889:100934`).
 *
 * PC 판(`/games/pc/games`, #5414)과 **데스크탑 필터가 다르다.** 저쪽은 칩 한 줄(지원
 * 방식과 장르 · 개수 없음)이고, 이쪽은 **상시 좌측 레일 + 항목별 개수**다(검색창도 레일
 * 안). 모바일은 둘 다 필터 시트다. 그래서 필터 훅(`usePcGamesFilters` ·
 * `useVrGamesFilters`)과 목록(`PcGamesList` · `VrGamesList`)은 따로 둔다.
 *
 * 나머지는 같다. 그리드는 둘 다 데스크탑 가로카드 3열(`laptop:grid-cols-3`)이고, 보기 모드
 * 기본값은 공용 `DEFAULT_VIEW_TYPE` 이다(#5653 · `game-view-type-default.test.ts` 가 두
 * 목록을 함께 잠근다). 검색 상태(`usePcSearchBox`)·드롭다운·빈 상태·투표·「Upcoming &
 * New」는 `pc-games/` 의 모듈을 그대로 쓴다(#5583 · #5599 · #5935).
 *
 * ⚠️ 종전 주석은 「저쪽은 칩 + 오버레이 시트에 세로카드 4열 … 공유하는 건 서브내비와
 * `GameCard` 뿐」이었다. 쓸 당시(#5416 `f1298379a` · 2026-08-31) PC 탭은 아직 레거시
 * `GameCardList`(드롭다운 + 오버레이 시트 · `laptop:grid-cols-4` · 기본 가로카드)였고, 다음
 * 날 #5414(`769cfae63`)가 칩 + 3열로 바꿨다 — 데스크탑 세로카드 4열은 어느 쪽 코드에도
 * 없었다(시안을 옮긴 서술이었는지는 리포로 확인할 수 없다). 「서브내비와 `GameCard` 뿐」은
 * 그때는 맞았고, 위 작업들이 공유 범위를 넓혔다.
 *
 * ⚠️ **레거시 `GameCardList` 를 쓰지 않았다**(#5689 가 삭제). 도입 당시엔 그 컴포넌트가
 * 쓰는 `useBhapticsContents` 가 필터 적용 시 `/games/search` 로 push 하도록 목적지가
 * 하드코딩돼 있어서, 탭 안에서 필터를 누르면 **탭 밖으로 튕겼다**(실측 2026-08-28 dev:
 * `/games/pc/games` → `Support Type > Native` 적용 → `/games/search/?filter=pc,native`).
 * #5448(`3532e15ac`, 2026-08-31)이 목적지를 `router.pathname` 으로 고쳐 지금은 사실이 아니다.
 * 여기는 `useVrGamesFilters` 가 현재 경로를 유지한다 — 그 훅을 따로 두는 사유는 그 파일 주석.
 */
/**
 * VR 탭의 교차탭 방향 — PC 판(`PcGamesList` 의 `PC_CROSS_TAB`)을 뒤집은 것.
 * 문구 6키는 #5614 가 7 로케일에 넣었다.
 */
const VR_CROSS_TAB = {
  title: "Supported as a PC game",
  desc: "These titles are supported on PC, not VR.",
  cta: "Check in PC",
} as const;

/**
 * ⑧ 빈 상태의 방향 — 위 ③ 과 **짝**이다 (#5599 · 2026-09-08).
 *
 * 종전엔 ③ 만 배선하고 그리드는 범용 `ContentsNotFound` 였다. 그러면 같은 화면이
 * 어긋난다 — 드롭다운은 「PC 에서 지원」이라 말하는데 그리드는 아무 말도 안 하고,
 * 드롭다운을 닫는 순간 그 정보가 사라진다.
 */
const VR_CROSS_TAB_EMPTY = {
  headline: "‘{{query}}’ is not in the VR support list",
  desc: "It is already supported as a PC game. Check the PC Games tab.",
  cta: "Check in PC Games",
} as const;

const VrGamesTabPage = ({ countryCode }: { countryCode: string }) => {
  const { t } = useTrans(["games"]);
  const state = useVrGamesFilters();
  // 상세를 보고 뒤로 왔을 때 눌렀던 카드 자리로 되돌린다 (#5651).
  useGamesListReturnAnchor();

  /**
   * ★**검색 드롭다운** (#5599 · 2026-09-07). PC 판과 **같은 컴포넌트**를 쓴다 —
   * 방향(③ 「PC 에서 지원」)만 `crossTab` 으로 뒤집는다. 사본을 두면 #5606 의
   * 「형제가 뒤처짐」이 재발한다(그 PR 이 실측으로 보여줬다).
   *
   * ⚠️ **여기서 한 번만 만든다.** 두 슬롯(레일·모바일 줄)에 **같은 노드**를 내려보내고,
   * `VrGamesSearch` 가 활성 슬롯에서만 그린다 — 이유·실측은 그 파일 주석.
   */
  const box = usePcSearchBox(state);
  const searchStates = useVrSearchStates(box.draft);

  /**
   * 투표 — 두 자리(드롭다운 ④ · 그리드 ⑧)가 같은 규약을 쓴다. 규약(비로그인이면 로그인으로 ·
   * 복귀 후 대기 투표 · 복귀 URL 의 `?q=`)은 PC 와 **같은 훅 하나**에 있다 — 자리마다
   * 사본을 두었더니 PC 그리드만 가드가 빠졌었다. 사유는 그 훅 주석.
   */
  const { onVote, votingId, checkingSignIn, voteFailure } = useGameRequestVote(
    state.draft,
  );

  /* ⑧ 그리드 빈 상태 — ③ 과 **같은 값**(입력 중인 draft)으로 유도한다. PC 판과 같다. */
  const emptySlot = (
    <PcGamesEmpty
      query={state.draft}
      states={searchStates}
      crossTab={VR_CROSS_TAB_EMPTY}
      browseAllHref={"/games/vr/games"}
      onVote={onVote}
      votingId={votingId}
      checkingSignIn={checkingSignIn}
      /* 로그인 복귀 후 대기 투표가 실패하는 자리가 여기다(복귀하면 이 블록으로 스크롤) —
         종전엔 드롭다운에만 문구가 있어 닫힌 패널 속에서 사라졌다. */
      voteFailure={voteFailure}
      /* 패널이 열려 있으면 실패 문구는 패널이 알린다(`role="alert"` 중복 방지). */
      searchPanelOpen={box.open}
    />
  );

  /* ⚠️ 절대배치 래퍼를 두지 않는다 — 패널 위치·외부 클릭·Escape·모바일 Dialog 는
     `GamesSearchShell` 이 담당한다(PC 와 같은 껍데기). */
  const searchDropdown = (
    <PcSearchDropdown
      query={box.draft}
      crossTab={VR_CROSS_TAB}
      states={searchStates}
      onPickKeyword={(k) => {
        box.setDraft(k);
        box.submit(k);
      }}
      onVote={onVote}
      votingId={votingId}
      checkingSignIn={checkingSignIn}
      voteFailure={voteFailure}
    />
  );

  /**
   * 모바일에서 **검색 + 필터 줄을 상단 스티키로** 올린다 (작성자 지정 — `/games/pc/games` 와 같게).
   *
   * ★**도킹(PC 방식)이 아니라 `sticky` 다.** 도킹은 줄을 띠 안으로 옮기는데, 그러면 띠가
   * 흐름에서 차지하는 높이가 늘어 **스크롤이 72px 튀고 그 점프가 툴바를 건너뛴다**
   * (사유·실측은 `use-sticky-band-bottom.ts` 주석). 흐름에 남기고 고정만 하면 점프가 0 이다.
   */
  const bandBottom = useStickyBandBottom();

  /**
   * 검색 + 필터 한 줄.
   *
   * ⚠️ 종전 사유(*"도킹 전/후가 같은 줄이어야 한다"*)는 **더 이상 성립하지 않는다** —
   * 도킹을 걷어서 「전/후」가 없고 사용처가 **한 곳**이다. 그래도 상수로 두는 이유는
   * 아래 `sticky` 래퍼가 위치·배경만 담당하고 **내용은 이 줄**이라는 구분을 유지해서다.
   */
  const mobileFilterRow = (
    <div className="flex items-center gap-2">
      <VrGamesSearch
        state={state}
        className="min-w-0 flex-1"
        slot="mobile"
        dropdown={searchDropdown}
        open={box.open}
        onOpenChange={box.setOpen}
        onSubmit={() => box.submit()}
        onClear={box.clear}
      />
      <VrGamesFilterSheet state={state} />
    </div>
  );

  return (
    <GamesPageTemplate countryCode={countryCode} tabBar={<GamesSubNav />}>
      {/* 폭 규약은 `VR_GAMES_CONTAINER`(파일 상단) 주석에 있다. */}
      <div className="flex w-full justify-center">
        <div className={VR_GAMES_CONTAINER}>
          {/**
           * `lp-head` — 데스크탑 `29889:100942` h=78 · 모바일 `29942:17600` h=76.
           *
           * ★**세로 간격 네 축을 좌표에서 다시 냈다.** 종전 값은 전부 어긋나 있었다
           * (실측 1920: 20 / 8 / 20). 시안은 좌표만 주므로 **앞 요소의 높이를 빼야**
           * 갭이 된다 — `docs/pitfalls.md` #40(Figma 구현은 섹션 사이 갭을 따로 재야 한다)과 같은 함정이다:
           *
           * ```
           *                      데스크탑                              모바일
           *   subnav→head   subnav 끝 118 → list-module 214   = 96    112 → 160        = 48
           *   제목→설명      제목 h44 → 설명 y56               = 12    h32 → y40        =  8
           *   head→body     head 끝 78 → lp-body y110         = 32    76 → 92          = 16
           *   하단→Footer    끝 1060 → Footer y1156            = 96    1679 → 1743      = 64
           * ```
           */}
          <header className="pb-4 pt-12 laptop:pb-8 laptop:pt-24">
            {/**
             * ★**타이포를 좌표에서 추측했다가 양쪽 다 틀렸다.**
             *
             * `get_metadata` 는 크기·굵기·색을 주지 않는다 — 높이 44 를 보고 「32/44」로
             * 읽었는데 실제로는 **36/44** 였고, 굵기도 700 이 아니라 **400** 이었다.
             * `get_design_context` 로 확인한 값(`docs/pitfalls.md` #40(Figma 구현은 섹션 사이 갭을 따로 재야 한다)):
             *
             * ```
             *            모바일 `29942:17601/17602`      데스크탑 `29889:100943/100944`
             *   제목      24 / 32  400  `#21272a`        36 / 44  400  `#121619`
             *   설명      14 / 18  400  `#4d5358` 0.25   16 / 22  400  `#4d5358` 0.5
             *
             *   종전     22/28 **700** coolgray-90       32/44 **700** coolgray-90
             *            13/18 coolgray-60              14/18 coolgray-60
             * ```
             *
             * 임의 px 를 쓰지 않는다 — 커스텀 스케일에 **정확히 맞는 토큰**이 있다:
             * `text-3xl`=24/32 · `text-6xl`=36/44 · `text-base`=14/18 · `text-lg`=16/22.
             *
             * ⚠️ `font-400` 을 명시한다. `font-bold`/`font-medium` 이 무효인 리포라
             * (`docs/pitfalls.md` #26(font-bold 는 이 프로젝트에서 무효)) 굵기는 숫자로만 쓰고, `<h1>` 의 브라우저 기본
             * 굵기에 기대지 않는다.
             */}
            {/**
             * ★**문구가 개정됐다** (2026-09-07 · `29889:100943/100944` ·
             * `29942:17601/17602` — **두 폭이 같은 문구**다):
             *
             * ```
             *   제목   `VR games you can play right now`
             *        → `Find the VR Game You Want to Play`
             *   설명   `Games not on the list can still be enjoyed with Audio To Haptic.`
             *        → `Explore supported VR games and enjoy more VR titles in haptics with Audio-to-Haptics.`
             * ```
             *
             * 종전 문구는 *"목록에 없어도 AtH 로 즐길 수 있다"* 쪽에 무게가 있었는데,
             * 개정본은 **찾기**를 제목으로 올리고 AtH 를 설명의 뒤쪽으로 내렸다 —
             * 이 화면이 목록/검색 화면이라는 것을 제목이 말한다.
             *
             * 옛 키 2개는 이 파일이 유일한 소비처였다(2026-09-07
             * `grep -rF '"VR games you can play right now"' src` = 1건) — 7로케일에서 함께 걷었다.
             *
             * ⚠️ 표기 주의: 시안은 **`Audio-to-Haptics`**(하이픈 2개 · 복수)다.
             * 옛 키는 `Audio To Haptic` 이었다 — 키가 영문 원문이라 표기가 곧 키다.
             */}
            <h1 className="text-3xl font-400 text-coolgray-90 laptop:text-6xl laptop:text-coolgray-100">
              {t("Find the VR Game You Want to Play")}
            </h1>
            <p className="mt-2 text-base font-400 tracking-[0.25px] text-coolgray-70 laptop:mt-3 laptop:text-lg laptop:tracking-[0.5px]">
              {t(
                "Explore supported VR games and enjoy more VR titles in haptics with Audio-to-Haptics.",
              )}
            </p>
          </header>
        </div>
      </div>

      {/**
       * ★「Upcoming & New」 — **PC 페이지 컴포넌트를 그대로 쓴다** (#5583 ·
       * 시안 데스크탑 `30376:63986` · 모바일 `30376:64058`).
       *
       * VR 시안을 노드 단위로 대조했더니 PC 와 **값이 전부 같다**(제목 22/28 · 화살표
       * 40/32 · 카드 220/160 · 포스터 2:3 · 캡션 갭 20/12 · 제목 1줄). 그래서 새로
       * 만들지 않고 플랫폼만 넘긴다 — 자세한 대조는 그 파일 주석에 있다.
       *
       * ## ★`VR_PLATFORM_VALUES` 를 넘긴다 — 값 하나로는 조용히 0건이다
       *
       * VR 은 단일 플랫폼 값이 아니라 6개 묶음이고, 이 페이지의 **목록도 같은 축**으로
       * 조회한다(`use-vr-games-filters.ts` `filters: [...VR_PLATFORM_VALUES]`).
       * `"vr"` 라는 facet 도 서버에 있지만 결과가 다르다(2026-09-07 실측 87 vs 67) —
       * 같은 화면의 목록과 어긋나지 않으려면 목록이 쓰는 축을 그대로 넘겨야 한다.
       *
       * ## ★컨테이너를 중첩하지 않는다 — 그래서 위 `div` 를 닫고 형제로 둔다
       *
       * 이 컴포넌트는 자기 컨테이너(`PC_GAMES_CONTAINER` = `mx-4 max-w-screen-max`)를
       * 갖는다. 위 컨테이너 **안**에 넣으면 `mx-4` 가 이중으로 걸려 좌우가 16px 씩 더
       * 들어가 헤더와 어긋난다.
       *
       * ## 여백을 `className` 으로 넘기는 이유
       *
       * 시안 갭은 데스크탑 **64** · 모바일 **48** 이다(PC 는 96/48 — 페이지마다 다르다):
       *
       * ```
       *   데스크탑 `29889:100940`   lp-head 끝 78 → upcoming y=142      = 64
       *                            upcoming 끝 590 → lp-body y=654     = 64
       *   모바일   `29942:17598`    lp-head 끝 108 → upcoming y=156     = 48
       *                            upcoming 끝 498 → 본문 y=546        = 48
       * ```
       *
       * `header` 가 이미 `pb-4 laptop:pb-8`(16/32)을 주므로 `pt-8`(32)을 더해 48/64 가
       * 된다. 아래쪽은 `pb-12 laptop:pb-16`(48/64).
       *
       * ★**래퍼 `div` 에 패딩을 주지 않는다.** 데이터가 비면 이 컴포넌트가 `null` 을
       * 돌려주는데, 래퍼에 주면 섹션이 사라져도 **빈 여백만 남는다.** 프롭으로 넘기면
       * 섹션과 함께 사라지고 갭이 종전(16/32)으로 돌아간다.
       */}
      <PcUpcomingAndNew
        platform={VR_PLATFORM_VALUES}
        className="pb-12 pt-8 laptop:pb-16"
      />

      <div className="flex w-full justify-center">
        <div className={VR_GAMES_CONTAINER}>
          {/**
           * 모바일 전용 행 — 시안 `29942:17603` (검색 272 + 간격 8 + 필터 48).
           *
           * 데스크탑은 검색이 **레일 안**에 있어서 이 행이 없다. 그래서 `laptop:hidden`
           * 이고, 반대로 레일은 `hidden laptop:block` 이다 — 검색 입력이 화면에 항상
           * **하나만** 보인다(같은 상태를 가리키는 입력이 둘 보이면 혼란스럽다).
           *
           * head(76 끝) → 이 행 y=92 라 갭 16 인데 `header` 의 `pb-4` 가 이미 준다.
           */}
          {/**
           * `top` 은 **띠 바닥**이다 — 띠의 `top` 이 스크롤로 움직이므로(헤더 숨김)
           * 상수를 쓸 수 없다. `z-10` 은 목록 위 · 띠(z-20) 아래.
           * `bg-white` 가 없으면 카드가 이 줄을 통과해 보인다.
           *
           * ⚠️ **`laptop:` 는 `laptop:hidden` 하나만 남긴다** (2026-09-07 정리).
           * 종전엔 `laptop:static laptop:z-auto`(+ 한때 `laptop:pb-4`)가 함께 있었는데
           * 이 줄은 그 폭에서 **숨으므로** 적용되는 조합이 없다 — 읽는 사람에게
           * 「데스크탑엔 다른 배치가 있다」로 읽히는 죽은 클래스다.
           *
           * ★위 여백 `py-3`(12)은 PC 모바일 도킹 줄과 같은 값이다. 종전 `pb-4` 는 위가
           * 0 이라 붙었을 때 트리거가 띠에 맞닿았다(사용자 보고 2026-09-07).
           */}
          <div
            /* ★결과 툴바가 이 줄의 바닥을 읽어 그 아래에 쌓인다 (#5561).
               선택자는 `sticky-band.ts` 단일 출처. */
            data-testid={MOBILE_FILTER_ROW_SELECTOR}
            className="sticky z-10 bg-white py-3 laptop:hidden"
            style={{ top: bandBottom }}
          >
            {mobileFilterRow}
          </div>

          {/* 시안 `29889:100945`(lp-body) — 레일↔목록 24px, 하단 96/64. */}
          <div className="flex flex-col gap-6 pb-16 laptop:flex-row laptop:items-start laptop:pb-24">
            <VrGamesFilterRail
              state={state}
              searchDropdown={searchDropdown}
              searchOpen={box.open}
              onSearchOpenChange={box.setOpen}
              onSearchSubmit={() => box.submit()}
              onSearchClear={box.clear}
            />
            <VrGamesList state={state} emptySlot={emptySlot} />
          </div>
        </div>
      </div>
    </GamesPageTemplate>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  return getGamesServerSideProps(ctx);
}

export default VrGamesTabPage;
